import React, { useState, useEffect } from 'react';
import { blockchainService, SystemStatus, TreasuryStats, VaultStats, AuditStats, TokenInfo } from '../services/blockchainService';
import { debugLog } from '../utils/config';

interface BlockchainStatusProps {
  refreshInterval?: number;
  onStatusChange?: (connected: boolean) => void;
  onDataFetched?: (data: {
    systemStatus: SystemStatus | null;
    treasuryStats: TreasuryStats | null;
    vaultStats: VaultStats | null;
    auditStats: AuditStats | null;
    tokenInfo: TokenInfo | null;
  }) => void;
}

const BlockchainStatus: React.FC<BlockchainStatusProps> = ({
  refreshInterval = 30000, // Increased default from 10s to 30s to reduce API calls
  onStatusChange,
  onDataFetched
}) => {
  const [systemStatus, setSystemStatus] = useState<SystemStatus | null>(null);
  const [treasuryStats, setTreasuryStats] = useState<TreasuryStats | null>(null);
  const [vaultStats, setVaultStats] = useState<VaultStats | null>(null);
  const [auditStats, setAuditStats] = useState<AuditStats | null>(null);
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setError(null);
      
      // Get system status first
      const status = await blockchainService.getSystemStatus();
      setSystemStatus(status);
      
      if (!status.connected) {
        setLoading(false);
        return;
      }

      // Fetch contract data if contracts are deployed
      const promises: Promise<any>[] = [];
      
      if (status.contractsDeployed.treasury) {
        promises.push(blockchainService.getTreasuryStats().catch(err => {
          debugLog('Failed to fetch treasury stats', err);
          return null;
        }));
      } else {
        promises.push(Promise.resolve(null));
      }
      
      if (status.contractsDeployed.vault) {
        promises.push(blockchainService.getVaultStats().catch(err => {
          debugLog('Failed to fetch vault stats', err);
          return null;
        }));
      } else {
        promises.push(Promise.resolve(null));
      }
      
      if (status.contractsDeployed.auditTrail) {
        promises.push(blockchainService.getAuditStats().catch(err => {
          debugLog('Failed to fetch audit stats', err);
          return null;
        }));
      } else {
        promises.push(Promise.resolve(null));
      }
      
      if (status.contractsDeployed.bwsToken && status.contractsDeployed.vault) {
        promises.push(blockchainService.getTokenInfo().catch(err => {
          debugLog('Failed to fetch token info', err);
          return null;
        }));
      } else {
        promises.push(Promise.resolve(null));
      }

      const [treasury, vault, audit, token] = await Promise.all(promises);

      setTreasuryStats(treasury);
      setVaultStats(vault);
      setAuditStats(audit);
      setTokenInfo(token);

      // Notify parent component of status change
      if (onStatusChange) {
        onStatusChange(status.connected);
      }

      // Pass all fetched data to parent
      if (onDataFetched) {
        onDataFetched({
          systemStatus: status,
          treasuryStats: treasury,
          vaultStats: vault,
          auditStats: audit,
          tokenInfo: token
        });
      }
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      debugLog('Error fetching blockchain data', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    
    const interval = setInterval(fetchData, refreshInterval);
    
    return () => clearInterval(interval);
  }, [refreshInterval, onStatusChange]);

  const formatAddress = (address: string): string => {
    if (!address) return 'Not configured';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatNumber = (value: string | number, decimals: number = 4): string => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '0';
    return num.toLocaleString(undefined, { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: decimals 
    });
  };

  if (loading) {
    return (
      <div className="card shadow-sm">
        <div className="card-body">
          <div className="placeholder-glow">
            <span className="placeholder col-3 mb-3"></span>
            <span className="placeholder col-12 mb-2"></span>
            <span className="placeholder col-10 mb-2"></span>
            <span className="placeholder col-9"></span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card shadow-sm border-danger">
        <div className="card-body">
          <div className="d-flex align-items-center gap-2 mb-3">
            <span className="badge bg-danger rounded-circle" style={{width: '12px', height: '12px'}}></span>
            <h3 className="h5 fw-semibold mb-0">Blockchain Connection Error</h3>
          </div>
          <p className="text-danger small mb-3">{error}</p>
          <button
            onClick={fetchData}
            className="btn btn-primary btn-sm"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  if (!systemStatus) {
    return null;
  }

  return (
    <>
      {/* Header Section */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-3">
          <span className={`badge rounded-circle ${
            systemStatus.connected ? 'bg-success' : 'bg-danger'
          }`} style={{width: '12px', height: '12px'}}></span>
          <h3 className="h5 fw-semibold mb-0">Blockchain Status</h3>
        </div>
        <button
          onClick={fetchData}
          className="btn btn-link text-muted p-0"
          title="Refresh data"
        >
          <i className="bi bi-arrow-clockwise"></i>
        </button>
      </div>

      {/* Grid of Cards */}
      <div className="row g-3">
        {/* Network Information Card */}
        <div className="col-12 col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h4 className="small fw-medium mb-3">Network Information</h4>
              <div className="row g-3 small">
                <div className="col-6">
                  <p className="text-muted mb-1">Network</p>
                  <p className="fw-medium mb-0">{systemStatus.networkName}</p>
                </div>
                <div className="col-6">
                  <p className="text-muted mb-1">Chain ID</p>
                  <p className="fw-medium mb-0">{systemStatus.chainId}</p>
                </div>
                <div className="col-6">
                  <p className="text-muted mb-1">Block Number</p>
                  <p className="fw-medium mb-0">{formatNumber(systemStatus.blockNumber, 0)}</p>
                </div>
                <div className="col-6">
                  <p className="text-muted mb-1">Status</p>
                  <p className={`fw-medium mb-0 ${
                    systemStatus.connected ? 'text-success' : 'text-body-secondary'
                  }`}>
                    {systemStatus.connected ? 'Connected' : 'Not Connected'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Smart Contracts Card */}
        <div className="col-12 col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h4 className="small fw-medium mb-3">Smart Contracts</h4>
              <div className="d-flex flex-column gap-2 small">
                <div className="d-flex justify-content-between align-items-center">
                  <span>Treasury</span>
                  <span className={`badge rounded-pill ${
                    systemStatus.contractsDeployed.treasury
                      ? 'bg-success-subtle text-success'
                      : 'bg-danger-subtle text-danger'
                  }`}>
                    {systemStatus.contractsDeployed.treasury ? 'Deployed' : 'Not Deployed'}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span>Market Making Vault</span>
                  <span className={`badge rounded-pill ${
                    systemStatus.contractsDeployed.vault
                      ? 'bg-success-subtle text-success'
                      : 'bg-danger-subtle text-danger'
                  }`}>
                    {systemStatus.contractsDeployed.vault ? 'Deployed' : 'Not Deployed'}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span>Audit Trail</span>
                  <span className={`badge rounded-pill ${
                    systemStatus.contractsDeployed.auditTrail
                      ? 'bg-success-subtle text-success'
                      : 'bg-danger-subtle text-danger'
                  }`}>
                    {systemStatus.contractsDeployed.auditTrail ? 'Deployed' : 'Not Deployed'}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span>BWS Token</span>
                  <span className={`badge rounded-pill ${
                    systemStatus.contractsDeployed.bwsToken
                      ? 'bg-success-subtle text-success'
                      : 'bg-danger-subtle text-danger'
                  }`}>
                    {systemStatus.contractsDeployed.bwsToken ? 'Deployed' : 'Not Deployed'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Treasury Stats Card */}
        {treasuryStats && (
          <div className="col-12 col-lg-6">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <h4 className="small fw-medium mb-3">Treasury Statistics</h4>
                <div className="row g-3 small">
                  <div className="col-6">
                    <p className="text-muted mb-1">Total Revenue</p>
                    <p className="fw-medium mb-0">{formatNumber(treasuryStats.totalDeposited)} ETH</p>
                  </div>
                  <div className="col-6">
                    <p className="text-muted mb-1">Authorized</p>
                    <p className="fw-medium mb-0">{formatNumber(treasuryStats.totalAuthorized)} ETH</p>
                  </div>
                  <div className="col-6">
                    <p className="text-muted mb-1">Available</p>
                    <p className="fw-medium mb-0">{formatNumber(treasuryStats.availableForAuthorization)} ETH</p>
                  </div>
                  <div className="col-6">
                    <p className="text-muted mb-1">Max %</p>
                    <p className="fw-medium mb-0">{treasuryStats.maxPurchasePercentage}%</p>
                  </div>
                </div>
                <div className="mt-3 small text-muted">
                  <p className="mb-1">Owner: {formatAddress(treasuryStats.owner)}</p>
                  <p className="mb-0">Status: {treasuryStats.paused ? 'Paused' : 'Active'}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Vault Stats Card */}
        {vaultStats && (
          <div className="col-12 col-lg-6">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <h4 className="small fw-medium mb-3">Market Making Vault</h4>
                <div className="row g-3 small">
                  <div className="col-6">
                    <p className="text-muted mb-1">ETH Received</p>
                    <p className="fw-medium mb-0">{formatNumber(vaultStats.totalEthReceived)} ETH</p>
                  </div>
                  <div className="col-6">
                    <p className="text-muted mb-1">Tokens Held</p>
                    <p className="fw-medium mb-0">{formatNumber(vaultStats.totalTokensHeld)} BWS</p>
                  </div>
                  <div className="col-6">
                    <p className="text-muted mb-1">Available</p>
                    <p className="fw-medium mb-0">{formatNumber(vaultStats.tokenUtilization.availableForWithdrawal)} BWS</p>
                  </div>
                  <div className="col-6">
                    <p className="text-muted mb-1">Status</p>
                    <p className="fw-medium mb-0">{vaultStats.paused ? 'Paused' : 'Active'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Audit Stats Card */}
        {auditStats && (
          <div className="col-12 col-lg-6">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <h4 className="small fw-medium mb-3">Trading Activity</h4>
                <div className="row g-3 small">
                  <div className="col-6">
                    <p className="text-muted mb-1">Total Purchases</p>
                    <p className="fw-medium mb-0">{formatNumber(auditStats.totalPurchases, 0)}</p>
                  </div>
                  <div className="col-6">
                    <p className="text-muted mb-1">ETH Spent</p>
                    <p className="fw-medium mb-0">{formatNumber(auditStats.totalEthSpent)} ETH</p>
                  </div>
                  <div className="col-6">
                    <p className="text-muted mb-1">Tokens Purchased</p>
                    <p className="fw-medium mb-0">{formatNumber(auditStats.totalTokensPurchased)} BWS</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Token Info Card */}
        {tokenInfo && (
          <div className="col-12 col-lg-6">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <h4 className="small fw-medium mb-3">BWS Token Information</h4>
                <div className="row g-3 small">
                  <div className="col-6">
                    <p className="text-muted mb-1">Name</p>
                    <p className="fw-medium mb-0">{tokenInfo.name}</p>
                  </div>
                  <div className="col-6">
                    <p className="text-muted mb-1">Symbol</p>
                    <p className="fw-medium mb-0">{tokenInfo.symbol}</p>
                  </div>
                  <div className="col-6">
                    <p className="text-muted mb-1">Total Supply</p>
                    <p className="fw-medium mb-0">{formatNumber(tokenInfo.totalSupply, 0)}</p>
                  </div>
                  <div className="col-6">
                    <p className="text-muted mb-1">Vault Balance</p>
                    <p className="fw-medium mb-0">{formatNumber(tokenInfo.vaultBalance, 0)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default BlockchainStatus;